import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage, isFilterRejected } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, useServerTabelData, useTabelStand, type ServerAntwoord } from '@/components/tabel';
import { usePhysicalItems, useRefreshPhysicalItems } from '../hooks/usePhysicalItems';
import { useProcessenFilter } from '../hooks/useProcessenFilter';
import { PROCESS_FILTER_TEKSTEN, processFilterTestId } from '../components/ProcessFilters';
import { formatFetchedAt } from '../format';
import { KetenKolommen } from '../components/KetenKolommen';
import { FYSIEK_IN_HUIS_SLEUTEL, FYSIEK_IN_HUIS_START, FYSIEKE_ITEMS_LIJST, PROCES_ITEMS_PAGINAGROOTTE, PROCESSEN_PAGINAFILTER, procesFilterVelden, fysiekeKetenPlek, stapkeuzesOpProcesvolgorde } from '../procesLijsten';
import type { PhysicalItem } from '../processes.api';

const START_LIJSTEN = [{ lijst: FYSIEK_IN_HUIS_START, standSleutel: FYSIEK_IN_HUIS_SLEUTEL }];

/**
 * R3 — de producten in het fysieke proces: alleen producten op lopende orders, elk in de stap waar
 * het nu staat (spec-processen-actuele-stand). Eén regel per product.
 *
 * Twee klokken met eigen naam: "in deze stap sinds" is informatief, stilstand telt pas vanaf
 * ontvangst (AD-24).
 *
 * spec-tabelfilters story 8: kolomfilters, gestapelde sortering en pagina in de lijststand
 * `fysiekInHuis`. De doorklik vanaf een stap op het fysieke procesdetail (`?step=`) wordt de
 * startfilter van de stapkolom; tegels en ketenkoppen tellen zonder kolomfilters ("n van m").
 */
export function FysiekInHuis() {
  const { t, i18n } = useTranslation();
  const kopRef = useRef<HTMLHeadingElement>(null);
  // Story 3: klant en drempel uit de gedeelde Processen-filter (story 12), zodat de doorklik vanaf de
  // tegel dezelfde verzameling laat zien (lijst-totaal = tegel).
  const { values: filterValues, wis, start, geweigerd } = useProcessenFilter(START_LIJSTEN);
  const stand = useTabelStand(FYSIEKE_ITEMS_LIJST, FYSIEK_IN_HUIS_SLEUTEL, {
    start: start[FYSIEK_IN_HUIS_SLEUTEL],
    afhankelijkVan: PROCESSEN_PAGINAFILTER,
    paginagrootte: PROCES_ITEMS_PAGINAGROOTTE,
  });
  const context = useMemo(() => ({ t: (s: string) => t(s) }), [t]);
  const velden = useMemo(() => procesFilterVelden(FYSIEKE_ITEMS_LIJST), []);

  // Het antwoord (totaal, facetten) komt na de vraag binnen en gaat via de stand een render later de hook in.
  const [antwoord, setAntwoord] = useState<ServerAntwoord>();
  const tabel = useServerTabelData(FYSIEKE_ITEMS_LIJST, stand, antwoord);
  const { page: _page, pageSize: _pageSize, ...kolommen } = tabel.parameters;
  // Stapkeuzes in ketenvolgorde, niet op label (besluit Friso 27-09-2026).
  const balkData = { ...tabel, keuzes: stapkeuzesOpProcesvolgorde(tabel.keuzes, stand.filters, fysiekeKetenPlek) };
  const itemsQuery = usePhysicalItems(tabel.pagina, { gln: filterValues.gln, stilstandDrempelWerkdagen: filterValues.stilstandDrempelWerkdagen }, undefined, kolommen);
  const refreshItems = useRefreshPhysicalItems(tabel.pagina);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const meta = itemsQuery.data?.data.meta;
  useEffect(() => {
    if (meta) setAntwoord({ totaal: meta.total, facetten: meta.facetten, suggesties: meta.suggesties, afgekapt: meta.afgekapt });
  }, [meta]);

  // Pas ná alle hooks een vroege uitgang: een hook achter een return wordt voorwaardelijk
  // aangeroepen, en dan crasht React zodra de pagina van foutstaat naar gevulde staat gaat.
  const filterRejected = isFilterRejected(itemsQuery.error, velden, itemsQuery.data?.data.sources.procesManagement);
  if (itemsQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={itemsQuery.error} onRetry={() => itemsQuery.refetch()} />;
  }

  const data = filterRejected ? undefined : itemsQuery.data?.data;
  // CAP-8: bij een onbereikbare bron geen totalen en geen lege staat — een 0 zou lezen als "niets in huis".
  const sourceUnavailable = data?.sources.procesManagementEvents?.status === 'unavailable';
  const kolomfilterActief = Object.keys(stand.filters).length > 0;

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await refreshItems();
    } finally {
      setIsRefreshing(false);
    }
  }

  /** Stapnaam uit de keten van R2; een stap of toestand daarbuiten valt terug op de ruwe code. */
  function stepLabelFor(item: PhysicalItem): string {
    const key = `processes.physical.steps.${item.stepKey}`;
    return i18n.exists(key) ? t(key) : item.canonicalStep;
  }

  return (
    <div className="space-y-6" data-testid="physical-items-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]" ref={kopRef} tabIndex={-1}>{t('processes.physicalItems.title')}</h1>
          <p className="text-xs text-[var(--foreground-muted)]">{t('processes.physicalItems.subtitle')}</p>
          {filterValues.gln != null && filterValues.gln !== '' ? (
            <p className="text-xs font-semibold text-[var(--foreground)]" data-testid="physical-items-customer-filter">
              {t('processes.physicalItems.filteredByCustomer', { gln: filterValues.gln })}
            </p>
          ) : null}
        </div>

        {data ? (
          <div className="flex items-center gap-3">
            <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="physical-items-updated-at">
              {t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
            </p>
            <Button data-testid="physical-items-refresh" disabled={isRefreshing} onClick={handleRefresh} size="sm" variant="secondary">
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">refresh</span>
              {t('processes.refresh')}
            </Button>
          </div>
        ) : null}
      </header>

      <FilterRejectedMessage
        baseTestId={processFilterTestId}
        error={itemsQuery.error}
        focusNaWissen={kopRef}
        linkGeweigerd={geweigerd}
        onClear={() => {
          wis();
          stand.reset();
        }}
        rejected={filterRejected}
        teksten={PROCESS_FILTER_TEKSTEN}
        velden={velden}
      />

      {/* Geweigerd filter: geen tabel, maar de filterbalk blijft, zodat het filter aan te passen is. */}
      {filterRejected ? <Filterbalk context={context} data={balkData} lijst={FYSIEKE_ITEMS_LIJST} stand={stand} /> : null}

      {itemsQuery.isLoading ? (
        <div className="h-64 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" data-testid="physical-items-loading" />
      ) : null}

      {data ? (
        <>
          {sourceUnavailable ? (
            <p className="text-xs text-[var(--info-text)]" data-testid="physical-items-degraded" role="status">{t('processes.degraded.title')}</p>
          ) : null}

          {data.meta.truncated ? (
            <p className="text-xs text-[var(--info-text)]" data-testid="physical-items-truncated" role="status">
              {t('processes.ordersTruncated.lijst')}
            </p>
          ) : null}

          {sourceUnavailable ? null : (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="physical-items-totals">
                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background)] p-3">
                  <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.physicalItems.totals.items')}</p>
                  <p className="text-2xl font-bold leading-none text-[var(--foreground)]" data-testid="physical-items-total">{data.totals.items}</p>
                </div>
                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background)] p-3">
                  <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.physicalItems.totals.notReceived')}</p>
                  <p className="text-2xl font-bold leading-none text-[var(--foreground)]" data-testid="physical-items-not-received">{data.totals.notReceived}</p>
                </div>
                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background)] p-3">
                  <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.physicalItems.totals.stagnating')}</p>
                  <p className="text-2xl font-bold leading-none text-[var(--foreground)]" data-testid="physical-items-stagnating">{data.totals.stagnating}</p>
                  <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.physicalItems.threshold', { count: data.totals.stagnationThresholdWorkdays })}</p>
                </div>
              </div>

              <Filterbalk context={context} data={balkData} lijst={FYSIEKE_ITEMS_LIJST} stand={stand} />

              {data.meta.total === 0 && kolomfilterActief ? (
                <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border)] bg-[var(--background-muted)] p-6 text-center text-sm text-[var(--foreground-muted)]" data-testid="physical-items-geen-treffers">
                  {t('tabel.geenTreffers')}
                </p>
              ) : data.meta.total === 0 ? (
                <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border)] bg-[var(--background-muted)] p-6 text-center text-sm text-[var(--foreground-muted)]" data-testid="physical-items-empty">
                  {t('processes.physicalItems.empty')}
                </p>
              ) : (
                <KetenKolommen
                  chain={data.chain}
                  items={data.items}
                  lijst={FYSIEKE_ITEMS_LIJST}
                  onSorteer={stand.klikOpKop}
                  sortering={stand.sortering}
                  stepLabelFor={stepLabelFor}
                  stepTotals={data.stepTotals}
                />
              )}

              {data.meta.total > data.meta.pageSize ? (
                <div className="flex flex-wrap items-center justify-between gap-3" data-testid="physical-items-pager">
                  <Button disabled={tabel.pagina <= 1} onClick={() => stand.zetPagina(tabel.pagina - 1)} size="sm" variant="secondary">
                    {t('common.previous')}
                  </Button>
                  <p className="text-xs text-[var(--foreground-muted)]">
                    {t('processes.physicalItems.pageOf', { page: tabel.pagina, total: Math.ceil(data.meta.total / data.meta.pageSize) })}
                    <span className="ml-2" data-testid="physical-items-treffers">{t('tabel.treffers', { count: data.meta.total })}</span>
                  </p>
                  <Button disabled={!data.meta.hasMore} onClick={() => stand.zetPagina(tabel.pagina + 1)} size="sm" variant="secondary">
                    {t('common.next')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
